/**
 * Keys that should never be merged to prevent prototype pollution attacks.
 * These keys can be used to modify the prototype chain and inject malicious properties.
 * See: https://owasp.org/www-community/vulnerabilities/Prototype_Pollution
 */
const FORBIDDEN_KEYS: ReadonlySet<string> = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Helper function to check if an object or any of its nested properties
 * contain a circular reference to any object in the seen set.
 *
 * @param obj - The object to check for circular references
 * @param seen - Set of objects already visited
 * @throws Error if a circular reference is detected
 */
function checkCircularReferences(obj: object, seen: Set<object>): void {
  // Check if this object itself is in the seen set
  if (seen.has(obj)) {
    throw new Error('deepMerge: Circular reference detected in source object');
  }

  // Don't recurse into special objects (Date, RegExp, etc.)
  if (obj instanceof Date || obj instanceof RegExp || Array.isArray(obj)) {
    return;
  }

  // Recursively check all properties
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
      continue;
    }

    const value = (obj as Record<string, unknown>)[key];
    if (value !== null && typeof value === 'object') {
      checkCircularReferences(value as object, seen);
    }
  }
}

/**
 * A type-safe, immutable deep merge function with circular reference and prototype pollution protection.
 *
 * Key behaviors:
 * - Returns a new object; does NOT mutate the target or source
 * - Arrays are replaced, not merged (source array replaces target array entirely)
 * - Circular references are detected and cause an error to be thrown
 * - undefined values in source are ignored (do not overwrite target)
 * - null values in source DO overwrite target values
 * - Date and RegExp objects are cloned properly to preserve their behavior
 *
 * Security features:
 * - Prototype pollution protection: Blocks __proto__, constructor, and prototype keys
 * - Circular reference protection: Uses a Set to track visited objects
 * - Only merges own properties using hasOwnProperty check
 *
 * @param target - The base object to merge into (not mutated)
 * @param source - The object with values to merge (not mutated)
 * @param seen - Internal Set used for circular reference detection (do not pass externally)
 * @returns A new merged object
 * @throws Error if a circular reference is detected in the source object
 */
export function deepMerge<T extends object>(target: T, source: Partial<T>, seen: Set<object> = new Set()): T {
  // Check for circular references in the source object
  if (seen.has(source)) {
    throw new Error('deepMerge: Circular reference detected in source object');
  }

  // Track this source object to detect cycles
  seen.add(source);

  // Start with a shallow copy of target to ensure immutability
  const result = { ...target };

  for (const key in source) {
    // Security: Skip prototype chain properties
    if (!Object.prototype.hasOwnProperty.call(source, key)) {
      continue;
    }

    // Security: Prevent prototype pollution attacks (CWE-1321)
    // Block dangerous keys that could modify the prototype chain
    if (FORBIDDEN_KEYS.has(key)) {
      continue;
    }

    const sourceValue = source[key as keyof T];
    const targetValue = result[key as keyof T];

    // Skip undefined values - they should not overwrite target
    if (sourceValue === undefined) {
      continue;
    }

    // Handle null - it should overwrite target
    if (sourceValue === null) {
      result[key as keyof T] = sourceValue as T[keyof T];
      continue;
    }

    // Handle arrays - replace entirely, don't merge
    if (Array.isArray(sourceValue)) {
      result[key as keyof T] = [...sourceValue] as T[keyof T];
      continue;
    }

    // Handle Date objects - preserve same reference (Date is immutable)
    if (sourceValue instanceof Date) {
      result[key as keyof T] = sourceValue as T[keyof T];
      continue;
    }

    // Handle RegExp objects - preserve same reference (RegExp is immutable)
    if (sourceValue instanceof RegExp) {
      result[key as keyof T] = sourceValue as T[keyof T];
      continue;
    }

    // Handle plain objects - recurse for deep merge
    if (typeof sourceValue === 'object') {
      // Check for circular reference BEFORE recursing or assigning
      if (seen.has(sourceValue as object)) {
        throw new Error('deepMerge: Circular reference detected in source object');
      }

      // Only recurse if target also has an object at this key
      // Otherwise, just assign the source value directly
      if (targetValue !== null && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
        // Recurse for nested objects, passing the seen Set to detect cycles
        result[key as keyof T] = deepMerge(
          targetValue as object,
          sourceValue as object,
          seen
        ) as T[keyof T];
      } else {
        // Target doesn't have an object here, so we can't merge - just assign
        // But we need to check for circular references in nested properties
        checkCircularReferences(sourceValue as object, seen);
        result[key as keyof T] = sourceValue as T[keyof T];
      }
    } else {
      // For primitives, functions, symbols, etc. - just copy the value
      result[key as keyof T] = sourceValue as T[keyof T];
    }
  }

  return result;
}